import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppContext = createContext();

export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [cart, setCart] = useState([]);
  const [currentBeat, setCurrentBeat] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sectionVisibility, setSectionVisibility] = useState({
    beats: true,
    services: true,
    courses: true,
    studio: true
  });

  // Optimized function to play a beat instantly
  const playBeat = useCallback((beat) => {
    const beatWithAudio = {
      ...beat,
      preview_url: beat.preview_url || beat.audio_url,
      audio_url: beat.audio_url || beat.preview_url
    };
    setCurrentBeat(beatWithAudio);
    setIsPlaying(true);
  }, []);

  useEffect(() => {
    checkAuth();
    loadCart();
    fetchSectionVisibility();
  }, []);

  const fetchSectionVisibility = async () => {
    try {
      const response = await axios.get(`${API}/settings/sections`);
      setSectionVisibility(response.data);
    } catch (e) {
      console.error('Failed to fetch section visibility', e);
    }
  };

  const refreshSectionVisibility = () => {
    fetchSectionVisibility();
  };

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const loadCart = () => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  };

  const addToCart = (item) => {
    const exists = cart.find(c => c.item_id === item.item_id && c.license_type === item.license_type);
    if (exists) {
      toast.info('Already in cart');
      return;
    }
    const newCart = [...cart, item];
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
    toast.success('Added to cart');
  };

  const removeFromCart = (itemId, licenseType) => {
    const newCart = cart.filter(c => !(c.item_id === itemId && c.license_type === licenseType));
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
    toast.success('Removed from cart');
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('cart');
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    setUser(null);
    toast.success('Logged out');
  };

  return (
    <AppContext.Provider value={{
      user, setUser, cart, addToCart, removeFromCart, clearCart,
      currentBeat, setCurrentBeat, isPlaying, setIsPlaying, playBeat, loading, logout, API,
      sectionVisibility, refreshSectionVisibility
    }}>
      {children}
    </AppContext.Provider>
  );
};

export { API };
